import { polar, pt, type Pt } from '../model/geom';
import { degToRad, fromDisplay, type DisplayUnit, type Micron } from '../model/units';

/**
 * 좌표 입력 문법 파서 (작업지시서 P2-04)
 *
 * ★ 기존 CAD 사용자가 "바로 쓸 수 있다"고 느끼는 지점은 리본 아이콘이 아니라
 *   **명령 입력 리듬**이다 (`고도화_제안_v2.md` 4.1절).
 *   이 파서가 AutoCAD 와 다르게 동작하면 아무리 아이콘이 같아도 이질감이 난다.
 *
 * 지원 문법 (AutoCAD 호환)
 *   1200,800     절대 직교좌표
 *   @1200,800    직전점 기준 상대좌표
 *   1500<45      절대 극좌표 (원점 기준)
 *   @1500<45     상대 극좌표 — 실무에서 가장 많이 쓴다
 *   <45          각도만 지정 (각도 고정)
 *   1200         거리만 — 커서가 가리키는 방향으로 (동적 입력)
 *
 * 각도는 도(°) 단위, 반시계 방향, +X축이 0°. 이것도 AutoCAD 관례다.
 */

export type ParsedInput =
  /** 확정된 점 */
  | { kind: 'point'; point: Pt }
  /** 거리만 입력됨. 방향은 호출자(커서 각도 또는 직교 모드)가 정한다. */
  | { kind: 'distance'; value: Micron }
  /** 각도만 입력됨 */
  | { kind: 'angle'; radians: number }
  /** 명령 옵션 키워드 */
  | { kind: 'keyword'; text: string }
  /** 빈 입력 (Enter 단독) */
  | { kind: 'empty' }
  | { kind: 'invalid'; text: string; reason: string };

export interface CoordContext {
  /** 직전 확정점. 상대좌표(@)의 기준. */
  lastPoint: Pt | null;
  /** 현재 커서 월드 좌표. 거리만 입력했을 때 방향을 준다. */
  cursor: Pt | null;
  displayUnit: DisplayUnit;
  /** 이 명령이 받는 옵션 키워드 목록 (대문자). 예: ['Fence','Crossing'] */
  keywords?: string[];
}

/** 숫자 하나를 표시 단위 기준으로 µm 정수로. 실패 시 null. */
function num(text: string, unit: DisplayUnit): Micron | null {
  const t = text.trim();
  if (t === '' || !/^[+-]?(\d+\.?\d*|\.\d+)$/.test(t)) return null;
  const v = Number(t);
  return Number.isFinite(v) ? fromDisplay(v, unit) : null;
}

/** 각도 문자열 → 라디안. 실패 시 null. */
function angle(text: string): number | null {
  const t = text.trim();
  if (t === '' || !/^[+-]?(\d+\.?\d*|\.\d+)$/.test(t)) return null;
  const v = Number(t);
  return Number.isFinite(v) ? degToRad(v) : null;
}

/**
 * 명령행/동적입력 문자열을 해석한다.
 * 공백은 무시하고, 대소문자는 키워드 매칭에서만 구분하지 않는다.
 */
export function parseCoordInput(raw: string, ctx: CoordContext): ParsedInput {
  const text = raw.trim();
  if (text === '') return { kind: 'empty' };

  const unit = ctx.displayUnit;

  /* ---- 상대 여부 ---- */
  const relative = text.startsWith('@');
  const body = relative ? text.slice(1).trim() : text;

  if (relative && !ctx.lastPoint) {
    return { kind: 'invalid', text, reason: '기준점이 없어 상대좌표를 쓸 수 없습니다' };
  }

  /* ---- 극좌표: [거리]<[각도] ---- */
  if (body.includes('<')) {
    const [dStr, aStr, ...rest] = body.split('<');
    if (rest.length > 0) {
      return { kind: 'invalid', text, reason: "극좌표는 '거리<각도' 형식입니다" };
    }

    const a = angle(aStr ?? '');
    if (a === null) {
      return { kind: 'invalid', text, reason: '각도를 읽을 수 없습니다' };
    }

    // "<45" — 각도만
    if ((dStr ?? '').trim() === '') {
      return { kind: 'angle', radians: a };
    }

    const d = num(dStr!, unit);
    if (d === null) {
      return { kind: 'invalid', text, reason: '거리를 읽을 수 없습니다' };
    }

    const base = relative ? ctx.lastPoint! : pt(0, 0);
    return { kind: 'point', point: polar(base, a, d) };
  }

  /* ---- 직교좌표: x,y ---- */
  if (body.includes(',')) {
    const parts = body.split(',');
    if (parts.length !== 2) {
      return { kind: 'invalid', text, reason: "좌표는 'x,y' 형식입니다" };
    }
    const x = num(parts[0]!, unit);
    const y = num(parts[1]!, unit);
    if (x === null || y === null) {
      return { kind: 'invalid', text, reason: '좌표 숫자를 읽을 수 없습니다' };
    }
    const base = relative ? ctx.lastPoint! : pt(0, 0);
    return { kind: 'point', point: pt(base.x + x, base.y + y) };
  }

  /* ---- 숫자 하나: 거리 (동적 입력) ---- */
  const d = num(body, unit);
  if (d !== null) {
    return { kind: 'distance', value: d };
  }

  /* ---- 키워드 ---- */
  const upper = body.toUpperCase();
  if (ctx.keywords && ctx.keywords.length > 0) {
    // 완전 일치 우선, 그다음 접두사 일치 (AutoCAD 는 첫 글자만 쳐도 먹는다)
    const exact = ctx.keywords.find((k) => k.toUpperCase() === upper);
    if (exact) return { kind: 'keyword', text: exact };
    const prefix = ctx.keywords.filter((k) => k.toUpperCase().startsWith(upper));
    if (prefix.length === 1) return { kind: 'keyword', text: prefix[0]! };
    if (prefix.length > 1) {
      return { kind: 'invalid', text, reason: `모호한 옵션입니다: ${prefix.join(', ')}` };
    }
  }

  return { kind: 'invalid', text, reason: '알 수 없는 입력입니다' };
}

/**
 * 거리 입력을 점으로 바꾼다 (동적 입력).
 * 방향은 ① 각도가 고정돼 있으면 그 각도, ② 아니면 커서 방향.
 */
export function distanceToPoint(
  distance: Micron,
  base: Pt,
  cursor: Pt | null,
  lockedAngle: number | null
): Pt | null {
  if (lockedAngle !== null) return polar(base, lockedAngle, distance);
  if (!cursor) return null;
  const dx = cursor.x - base.x;
  const dy = cursor.y - base.y;
  if (dx === 0 && dy === 0) return null;
  return polar(base, Math.atan2(dy, dx), distance);
}
